import { create } from 'zustand'
import type { HanziConfig } from '../types'
import {
  serializeConfig,
  deserializeConfig,
  saveConfigToStorage,
  loadConfigFromStorage,
} from '../utils/url-config'
import { fetchShortLink } from '../utils/share-api'

interface HistoryItem {
  id: string
  chars: string
  timestamp: number
  preview: string
  paperSize: string
}

interface HanziState {
  config: HanziConfig
  history: HistoryItem[]
  isLoadingShortLink: boolean
  updateConfig: (partial: Partial<HanziConfig>) => void
  setChars: (chars: string) => void
  setWordMode: (chars: string, words: string[]) => void
  setCharMode: (chars: string) => void
  restoreHistory: (item: HistoryItem) => void
  deleteHistory: (id: string) => void
}

const defaultConfig: HanziConfig = {
  chars: '我爱学习汉字',
  gridType: 'mi',
  traceCount: 3,
  insertBlankRow: false,
  insertBlankCol: false,
  showStrokeOrder: false,
  traceColor: '#E74C3C',
  lineColor: '#9E9E9E',
  paperSize: 'a4',
  fontFamily: 'wenkai' as const,
  showPinyin: false,
  inputMode: 'char',
}

function getInitialConfig(): HanziConfig {
  const urlParams = new URLSearchParams(window.location.search)
  if (urlParams.toString()) {
    const deserialized = deserializeConfig(urlParams)
    if (Object.keys(deserialized).length > 0) {
      return { ...defaultConfig, ...deserialized }
    }
  }

  const stored = loadConfigFromStorage()
  if (stored) {
    return { ...defaultConfig, ...stored }
  }

  return defaultConfig
}

function getInitialHistory(): HistoryItem[] {
  try {
    const raw = localStorage.getItem('zitie:history')
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function saveHistoryToStorage(history: HistoryItem[]) {
  try {
    localStorage.setItem('zitie:history', JSON.stringify(history.slice(0, 20)))
  } catch {}
}

function addHistoryEntry(history: HistoryItem[], config: HanziConfig): HistoryItem[] {
  if (!config.chars || config.chars.length === 0) return history
  const newItem: HistoryItem = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    chars: config.chars.slice(0, 20) + (config.chars.length > 20 ? '...' : ''),
    timestamp: Date.now(),
    preview: config.chars.slice(0, 8) + (config.chars.length > 8 ? '...' : ''),
    paperSize: config.paperSize,
  }
  const filtered = history.filter((h) => h.chars !== newItem.chars)
  return [newItem, ...filtered].slice(0, 20)
}

export const useHanziStore = create<HanziState>((set) => ({
  config: getInitialConfig(),
  history: getInitialHistory(),
  isLoadingShortLink: false,
  updateConfig: (partial) =>
    set((state) => {
      const next = { ...state.config, ...partial }
      const newHistory =
        partial.chars && partial.chars !== state.config.chars
          ? addHistoryEntry(state.history, next)
          : state.history
      if (newHistory !== state.history) saveHistoryToStorage(newHistory)
      syncUrlAndStorage(next)
      return { config: next, history: newHistory }
    }),
  setChars: (chars) =>
    set((state) => {
      const next = { ...state.config, chars, words: undefined, inputMode: 'char' as const }
      syncUrlAndStorage(next)
      return { config: next }
    }),
  setWordMode: (chars, words) =>
    set((state) => {
      const next = {
        ...state.config,
        chars,
        words,
        inputMode: 'word' as const,
        showStrokeOrder: false,
        showPinyin: false,
      }
      const newHistory = addHistoryEntry(state.history, next)
      saveHistoryToStorage(newHistory)
      syncUrlAndStorage(next)
      return { config: next, history: newHistory }
    }),
  setCharMode: (chars) =>
    set((state) => {
      const next = { ...state.config, chars, words: undefined, inputMode: 'char' as const }
      const newHistory = addHistoryEntry(state.history, next)
      saveHistoryToStorage(newHistory)
      syncUrlAndStorage(next)
      return { config: next, history: newHistory }
    }),
  restoreHistory: (_item) =>
    set((state) => {
      const stored = loadConfigFromStorage()
      return { config: stored ? { ...state.config, ...stored } : state.config }
    }),
  deleteHistory: (id) =>
    set((state) => {
      const newHistory = state.history.filter((h) => h.id !== id)
      saveHistoryToStorage(newHistory)
      return { history: newHistory }
    }),
}))

function syncUrlAndStorage(config: HanziConfig) {
  const qs = serializeConfig(config)
  const newUrl = qs ? `${window.location.pathname}?${qs}` : window.location.pathname
  window.history.replaceState({}, '', newUrl)
  saveConfigToStorage(config)
}

export async function initShortLink(): Promise<boolean> {
  const urlParams = new URLSearchParams(window.location.search)
  const shortId = urlParams.get('s')
  if (!shortId) return false

  const store = useHanziStore.getState()
  store.updateConfig({ ...store.config })

  try {
    useHanziStore.setState({ isLoadingShortLink: true })
    const result = await fetchShortLink(shortId)
    if (result.ok && result.config) {
      const next = { ...defaultConfig, ...result.config }
      useHanziStore.setState({ config: next, isLoadingShortLink: false })
      saveConfigToStorage(next)
      return true
    }
  } catch {
  }

  useHanziStore.setState({ isLoadingShortLink: false })
  const cleanUrl = window.location.pathname
  window.history.replaceState({}, '', cleanUrl)
  return false
}

